import { PriceFunc, PriceElemsData, PriceSegment, getConstrElems, getListPrice, PriceException } from '@icc/price';
import { CustomPricesRecords } from '@icc/shared/custom-price/CustomPrice';
import camelCase from 'camelcase';
import { Injectable, Inject } from '@angular/core';
import {APP_CONFIG, AppConfig, AppConfigFactory} from '@icc/shared/config';;
import { ColorsDefaultsService } from '../colors/colors-defaults.service';
import { Common } from '@icc/shared/helpers';
import { core } from '@icc/shared/helpers';
import { ColorDto as IccColor, ColorGroupDto as IccColorGroup, ConstructColorDto as IccConstructColor, WoodDto as IccWood } from '@icc/construction-elements/types';
import { IccProfileSideColors, IccSideColors, IccWindowColors } from '@icc/construction-elements/types';
import { Side, Target } from '@icc/construction-elements/types';
import { Profile } from '@icc/window-door/types';
import { ActiveSash } from '@icc/window-door/types';

@Injectable()
export class PriceColorsService {
    constructor(
        private colorsDefaultsService: ColorsDefaultsService,
        @Inject(APP_CONFIG) private config: AppConfigFactory
    ) {}

    /**
     * Zwraca najwyższą dopłatę procentową dla koloru.
     * @param  {object} colorGroups Grupy kolorów
     * @param  {object} color       Kolor
     * @param  {string} prop        Nazwa pola zawierającego dopłatę.
     * @return {number}             Dopłata procentowa dla koloru
     */
    // eslint-disable-next-line max-statements
    getMinMaxColorGroupFactor({
        colorGroups,
        color,
        prop,
        system,
        wood,
        sash,
        sashId,
        divId,
        drawData,
        sashes,
        mullions,
        customPrice,
        side,
        minimal,
        target,
        listPrice = false,
    }: {
        colorGroups: IccColorGroup[];
        color: IccConstructColor | IccColor | null;
        prop;
        system;
        wood: IccWood | null;
        sash: ActiveSash[];
        sashId;
        divId;
        drawData;
        sashes;
        mullions;
        customPrice: CustomPricesRecords;
        side?;
        minimal?: boolean;
        target?: Target;
        listPrice?: boolean;
    }) {
        let colorFactor = null;
        let colorFactorExtra = null;
        let colorFactorExtraProfile = 0;
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;
        if (!Common.isArray(sash) || !color) {
            return NaN;
        }
        if (!colorGroups) {
            colorGroups = [];
        }
        colorGroups = colorGroups.filter(el => {
            const systems = Common.isArray(el.systems) ? el.systems : [];
            const targets = Common.isArray(el.target) ? el.target : [];
            const sides = Common.isArray(el.sides) ? el.sides : [];
            const woodTypes = Common.isArray(el.woodTypes) ? el.woodTypes : [];
            const hasWoodTypes = woodTypes.length > 0;
            const hasSystems = systems.length > 0;
            const systemMatch = !system?.id || systems.indexOf(system.id) > -1;
            const targetValue = target ? target : 'price';
            const targetMatch = targets.indexOf(targetValue) > -1;
            const sideMatch = side == null || sides.includes(side);
            let woodMatch = true;
            if (system?.type === 'wood') {
                woodMatch = wood?.id != null && woodTypes.map(Number).indexOf(Number(wood.id)) > -1;
            }
            const result = hasSystems && systemMatch && targetMatch && sideMatch && woodMatch;
            return result;
        });
        const groups = 'groups' in color ? color.groups : color.colorGroups;
        if (color && Common.isArray(groups)) {
            for (let i = 0; i < groups.length; i++) {
                const group = colorGroups.find(el => Number(el.id) === Number(groups[i]));
                if (Common.isDefined(group)) {
                    let customColorFactor = null;
                    if (Common.isObject(customPrice) && customPrice[group.id]) {
                        customColorFactor = customPrice[group.id].getPrice(prop);
                    }
                    if (customColorFactor != null && customColorFactor >= colorFactor) {
                        colorFactor = customColorFactor;
                    }
                    if (customColorFactor == null && Common.isDefined(group[prop])) {
                        const groupProp = getListPrice(group, prop, listPrice, listPriceEnableInMarket);
                        if (groupProp != null && parseFloat(groupProp) >= colorFactor) {
                            colorFactor = parseFloat(groupProp);
                        }
                    }
                    if (Common.isDefined(group.prices_extra)) {
                        const pricesExtra = group.prices_extra
                            ? core.parseJson(group.prices_extra)
                            : [];
                        sash.forEach(s => {
                            const colorSashFactor = pricesExtra
                                .filter(
                                    p => p.sashTypes && p.sashTypes.map(Number).indexOf(~~s) > -1
                                )
                                .reduce((prev, cur, index) => {
                                    if (Common.isObject(customPrice) && customPrice[group.id]) {
                                        const customColorSashFactor = customPrice[
                                            group.id
                                        ].getPrice('prices_extra', index, camelCase(prop));
                                        return (minimal ? prev > customColorSashFactor : prev < customColorSashFactor)
                                            ? customColorSashFactor
                                            : prev;
                                    } else {
                                        const curPrice = getListPrice(cur, camelCase(prop), listPrice, listPriceEnableInMarket,  camelCase('list_' + prop));
                                        return (minimal ? prev >  curPrice : prev < curPrice)
                                            ? curPrice
                                            : prev;
                                    }
                                }, 0);
                            if (minimal ? colorSashFactor < colorFactorExtra : colorSashFactor > colorFactorExtra) {
                                colorFactorExtra = colorSashFactor;
                            }
                        });
                    }
                    if (Common.isDefined(group.prices_extra_profiles)) {
                        const pricesExtraProfiles = group.prices_extra_profiles
                            ? core.parseJson(group.prices_extra_profiles)
                            : [];
                        const profilesIds = [];
                        sashId.forEach(s => {
                            const profilesForSash = this.getProfilesForSash(s, drawData, sashes);
                            profilesIds.push(
                                ...profilesForSash.filter(p => !profilesIds.includes(p))
                            );
                        });
                        divId.forEach(s => {
                            const profileForMullion = this.getProfileForMullion(s, mullions);
                            if (!profilesIds.includes(profileForMullion)) {
                                profilesIds.push(profileForMullion);
                            }
                        });
                        colorFactorExtraProfile += profilesIds.reduce((prev, profileId) => {
                            const profileSashFactor = pricesExtraProfiles
                                .filter(p => p.profiles.map(Number).indexOf(~~profileId) > -1)
                                .reduce((p, cur, index) => {
                                    let factor = getListPrice(cur, camelCase(prop), listPrice, listPriceEnableInMarket,  camelCase('list_' + prop));
                                    if (Common.isObject(customPrice) && customPrice[group.id]) {
                                        const customProfileSashFactor = customPrice[
                                            group.id
                                        ].getPrice('prices_extra', index, camelCase(prop));
                                        if (customProfileSashFactor !== null) {
                                            factor = customProfileSashFactor;
                                        }
                                    }
                                    return p + factor;
                                }, 0);
                            return prev + profileSashFactor;
                        }, 0);
                    }
                }
            }
        }
        if (colorFactor == null && colorFactorExtra == null) {
            return NaN;
        }
        return (colorFactorExtra || colorFactor) + colorFactorExtraProfile;
    }

    /**
     * Zwraca dopłatę procentową za RAL.
     * @param  {array}  colorGroups   Grupy kolorów
     * @return {number}               Cena po dopłatach
     */
    getRALGroup(colorGroups) {
        colorGroups = core.objToArray(colorGroups);
        const RALGroup = core.fIdO<any>(colorGroups, 'ral', 'code');
        return RALGroup;
    }


    getColorFactor({
        NoPriceCauses,
        isAlushell,
        colorGroups,
        colorGroupsPrices,
        colors,
        system,
        wood,
        sash,
        sashId,
        divId,
        drawData,
        sashes,
        mullions,
        customPrice,
        listPrice = false
    }: {
        NoPriceCauses;
        isAlushell;
        colorGroups;
        colorGroupsPrices;
        colors: IccWindowColors;
        system;
        wood;
        sash;
        sashId;
        divId;
        drawData;
        sashes;
        mullions;
        customPrice?: CustomPricesRecords;
        listPrice?: boolean;
    }) {
        let colorFactor = 0
        let hasBicolorGroupsPrice = false;
        if (!isAlushell && colors.frame.outer?.id !== colors.frame.inner?.id && !colors.frame.outer?.isCore && !colors.frame.inner?.isCore) {
            colorFactor = this.getColorGroupsPrice(colorGroupsPrices, colors, listPrice);
            hasBicolorGroupsPrice = !isNaN(colorFactor);
        }
        if (isNaN(colorFactor) || !hasBicolorGroupsPrice || isAlushell) {
            colorFactor = this.getMinMaxColorGroupFactor({
                colorGroups,
                color: colors.frame[this.getColorSide(colors.frame, false, isAlushell)],
                prop: isAlushell ? 'price_factor_out' : this.getColorFactorProperty(colors.frame, isAlushell),
                system,
                wood,
                sash,
                sashId,
                divId,
                drawData,
                sashes,
                mullions,
                customPrice,
                side: isAlushell ? 'F|A' : null,
                listPrice
            });

            if (colors.frame.outer?.id && colors.frame.inner?.id && colors.frame.outer?.id !== colors.frame.inner?.id) {
                const colorFactorOut = this.getMinMaxColorGroupFactor({
                    colorGroups,
                    color: colors.frame[this.getColorSide(colors.frame, true)],
                    prop: this.getColorFactorProperty(colors.frame),
                    system,
                    wood,
                    sash,
                    sashId,
                    divId,
                    drawData,
                    sashes,
                    mullions,
                    customPrice,
                    listPrice
                });
                colorFactor =
                        colorFactorOut > colorFactor || isNaN(colorFactorOut)
                            ? colorFactorOut
                            : colorFactor;
            }
        }

        if (isNaN(colorFactor)) {
            NoPriceCauses.push('color factor');
        }

        return colorFactor;
    }
    getColorSide(colors: Partial<IccProfileSideColors>, outer = false, isAlushell = false) {
        if (isAlushell) {
            return 'alushell';
        } else if (!colors.outer?.id && !colors.inner?.id) {
            return 'core';
        } else if (colors.outer?.id === colors.inner?.id) {
            return 'inner';
        } else if (!colors.outer?.id || colors.outer?.sides.includes(Side.FC)) {
            return 'inner';
        } else if (!colors.inner?.id || colors.inner?.sides.includes(Side.FC)) {
            return 'outer';
        } else if (outer) {
            return 'outer';
        }
        return 'inner';
    }

    getColorFactorProperty(colors: Partial<IccProfileSideColors> | {
        outer?: IccColor,
        inner?: IccColor,
    }, isAlushell = false) {

        if (colors.outer?.id === colors.inner?.id) {
            return 'price_factor_both';
        } else if (!colors.outer?.id || colors.outer?.isCore || 'sides' in colors.outer && colors.outer?.sides.includes(Side.FC)) {
            return 'price_factor_in';
        }  else if (!colors.inner?.id || colors.inner?.isCore || 'sides' in colors.inner && colors.inner?.sides.includes(Side.FC) || isAlushell) {
            return 'price_factor_out';
        }
        return 'price_factor_duo';
    }

    getColorGroupsPrice(colorGroupsPrices, colors, listPrice = false) {
        const listPriceEnableInMarket = this.config().IccConfig.Offer.listPrice ? this.config().listPriceEnableInMarket : true;
        return (colorGroupsPrices ? colorGroupsPrices : [])
            .filter(
                el =>
                    colors.frame.inner.groups
                    && colors.frame.inner.groups.indexOf(Number(el.window_color_group_inner_id)) > -1
                    && colors.frame.outer.groups
                    && colors.frame.outer.groups.indexOf(Number(el.window_color_group_outer_id)) > -1
            )
            .reduce((prev, curr) => {
                const price = Number(getListPrice(curr, 'price', listPrice, listPriceEnableInMarket));
                if (isNaN(prev) || price > prev) {
                    prev = price;
                }
                return prev;
            }, NaN);
    }

    /**
     * Dopłata procentowa za kolor.
     *
     * @param {number} price         Cena wejściowa
     * @param {object} PriceElems    Składowe ceny
     * @param {array}  NoPriceCauses Powody braku wyceny
     * @param {array}  constrElems   Lista elementów konstrukcyjnych, do przeliczenia przez dopłatę.
     * @param {array}  colors        Kolory
     * @param {array}  colorGroups   Grupy kolorów
     * @param {number} sashesCount   Liczba skrzydeł w konstrukcji
     * @return {number} Cena z dopłatą
     */
    @PriceFunc({
        shortName: 'color',
        data: {
            colors: 'conf.Colors',
            colorGroups: 'data.windowColorGroups',
            colorGroupsPrices: 'data.windowColorGroupsPrices',
            sashesCount: 'conf.Sashes.length',
            system: 'conf.System',
            wood: 'conf.Wood',
            drawData: 'conf.drawData',
            sashes: 'conf.Sashes',
            customPrice: 'price.WindowColorGroup',
            mullions: 'conf.Mullions',
        },
    })
    suppColor(
        { PriceStack, NoPriceCauses, listPrice }: PriceElemsData,
        {
            colors,
            colorGroups,
            colorGroupsPrices,
            sashesCount,
            system,
            wood,
            drawData,
            sashes,
            mullions,
            customPrice,
        }: {
            colors: IccWindowColors;
            colorGroups;
            colorGroupsPrices;
            sashesCount;
            system;
            wood;
            drawData;
            sashes;
            mullions;
            customPrice: CustomPricesRecords;
        }
    ): PriceSegment[] {
        const constrElems = getConstrElems(this.config().IccConfig, system, 'color');
        const priceSashes = PriceStack.filter(
            s => constrElems.indexOf('sashes') > -1 && s.type === 'sashes'
        );
        const divs = PriceStack.filter(
            s => constrElems.indexOf('dividers') > -1 && s.type === 'dividers'
        );
        const priceSegments: PriceSegment[] = [];
        const divFactors: any = [];
        if (!colorGroups || colorGroups.length === 0) {
            return [];
        }
        if (colorGroupsPrices == null) {
            colorGroupsPrices = [];
        }
        const colorFactorDefault = this.getColorFactor({
            NoPriceCauses,
            isAlushell: false,
            colorGroups,
            colorGroupsPrices,
            colors,
            system,
            wood,
            sash: [],
            sashId: [],
            divId: [],
            drawData,
            sashes,
            mullions,
            customPrice,
            listPrice,
        });
        priceSashes.forEach(s => {    
            const colorFactor = this.getColorFactor({
                NoPriceCauses,
                isAlushell: false,
                colorGroups,
                colorGroupsPrices,
                colors,
                system,
                wood,
                sash: s.data.types,
                sashId: s.data.sashIds,
                divId: s.data.divIds || [],
                drawData,
                sashes,
                mullions,
                customPrice,
                listPrice
            });
            if (s.data && s.data.divIds) {
                divFactors.push({
                    divs: s.data.divIds || [],
                    factor: colorFactor,
                });
            }
            priceSegments.push({
                type: 'color',
                baseValue: !isNaN(colorFactor) ? 1 + colorFactor / 100 : null,
                value: !isNaN(colorFactor) ? 1 + colorFactor / 100 : null,
                valueType: 'percent',
                data: {},
                to: s.id,
            });
        });
        divs.forEach(d => {
            const factor = divFactors.filter(df => df.divs.indexOf(d.data.divId) > -1);
            if (factor.length > 0) {
                priceSegments.push({
                    type: 'color',
                    baseValue: !isNaN(factor[0].factor) ? 1 + factor[0].factor / 100 : null,
                    value: !isNaN(factor[0].factor) ? 1 + factor[0].factor / 100 : null,
                    valueType: 'percent',
                    data: {},
                    to: d.id,
                });
            }
        });
        priceSegments.push({
            type: 'color',
            baseValue: !isNaN(colorFactorDefault) ? 1 + colorFactorDefault / 100 : null,
            value: !isNaN(colorFactorDefault) ? 1 + colorFactorDefault / 100 : null,
            valueType: 'percent',
            data: {},
            to: constrElems.filter(s => s !== 'sashes' && s !== 'dividers'),
        });
        return priceSegments;
    }


    getSideProfileColorFactor({
        profile,
        profileColors,
        wood,
        system,
        colorGroups,
    }: {
        profile: Profile;
        profileColors: IccProfileSideColors;
        wood: { id?: string | number | null } | undefined;
        system: any;
        colorGroups: IccColorGroup[];
    }) {
        if (profile.type !== 'extension' && profile.type !== 'coupling') {
            return 0;
        }

        if (!this.config().IccConfig.Configurators.price.colorFactorToSideProfile) {
            return 0;
        }

        let colorFactor = 0;

        if (colorGroups?.length) {
            colorFactor = this.getColorFactor({
                NoPriceCauses: [],
                isAlushell: false,
                colorGroups,
                colorGroupsPrices: [],
                colors: { frame: profileColors, sash: profileColors },
                system,
                wood,
                sash: [],
                sashId: [],
                divId: [],
                drawData: null,
                sashes: [],
                mullions: [],
            });
        }
        return colorFactor;
    }

    /**
     * Dopłata za typ drzewa
     * @param  {Number} price           Cena
     * @param  {Object} PriceElems      Wycena
     * @param  {Array} constrElems      Elementy konstrukcyjne
     * @param  {Object} wood            Drzewo
     * @return {Number}                 Cena
     */
    @PriceFunc({
        shortName: 'wood',
        data: {
            wood: 'conf.Wood',
            system: 'conf.System',
            customPrice: 'price.WoodType',
        },
    })
    suppWoodType(
        { PriceElems }: PriceElemsData,
        { wood, system, customPrice }: { wood; system; customPrice: CustomPricesRecords }
    ): PriceSegment[] {
        let factors = 1;
        let woodCharge = 0;
        if (system.type !== 'wood') {
            return [];
        }
        if (Common.isObject(wood)) {
            if (Common.isObject(customPrice) && customPrice[wood.id]) {
                woodCharge = customPrice[wood.id].getPrice('charge');
            } else {
                woodCharge = parseFloat(wood.charge);
            }
            PriceElems.wood = {
                id: wood.id,
                factor: woodCharge,
                name: wood.name,
            };
            factors *= 1 + woodCharge / 100;
        }

        return [
            {
                type: 'wood',
                baseValue: factors,
                value: factors,
                valueType: 'percent',
                data: {
                    id: wood.id,
                    name: wood.name,
                },
            },
        ] as PriceSegment[];
    }

    private getProfilesForSash(sashId, drawData, sashes) {
        const profilesForSash = [];
        const sashData = drawData.sash.find(o => o.sashId === sashId);
        if (sashData) {
            sashData.parallel.poly.forEach((frameIndex, sashIndex) => {
                if (Common.isNumber(frameIndex)) {
                    const frameProfileId = sashData.parallel.profiles[frameIndex];
                    if (!profilesForSash.includes(frameProfileId)) {
                        profilesForSash.push(frameProfileId);
                    }
                }
            });
            const sash = sashes.find(s => s.id === sashId);
            if (sash.type.type !== 'F') {
                ['left', 'right', 'top', 'bottom']
                    .filter(side => sash.frame[side])
                    .forEach(side => {
                        const sashProfileId = sash.frame[side].profileId;
                        if (!profilesForSash.includes(sashProfileId)) {
                            profilesForSash.push(sashProfileId);
                        }
                    });
            }
        }

        return profilesForSash;
    }

    private getProfileForMullion(mullionId, mullions) {
        const mullion = mullions.find(m => m.id === mullionId);
        return mullion.profileId;
    }
}
